/**
 * Lab Dashboard Types
 * Shared type definitions and constants for the lab dashboard hooks
 */

import type { DirectionResult, MarketCondition } from '@parlay-picker/shared/marketCondition';
import type { LeagueMarketCondition } from '@parlay-picker/shared/marketDayLabels';
import type { VoidReason } from '@parlay-picker/shared/settlement';

// =============================================================================
// Core Filter Types
// =============================================================================

// Scope defines which picks to include in analytics
// - 'all': All AI picks (platform-wide)
// - 'tracked': All tracked picks across all users (platform-wide tracked)
// - 'user': Only picks tracked by a specific user (requires userId)
export type AnalyticsScope = 'all' | 'tracked' | 'user';

export interface LabFilters {
  dateRange: string;
  config: string;
  tier: string;
  propType: string;
  direction: string;
  minSampleSize: number;
  scope: AnalyticsScope;
  userId: string | null; // Required when scope is 'user' (UUID)
}

export const DEFAULT_FILTERS: LabFilters = {
  dateRange: 'all',
  config: '',
  tier: '',
  propType: '',
  direction: '',
  minSampleSize: 5,
  scope: 'all', // Default to showing all AI picks
  userId: null, // No user filter by default
};

// User with tracked picks (for admin user selector)
export interface UserWithTrackedPicks {
  id: string; // UUID
  email: string;
  trackedCount: number;
}

// =============================================================================
// Dashboard Analytics Types
// =============================================================================

export interface ConfigPerformance {
  configVariation: string;
  totalPicks: number;
  settledPicks: number;
  hits: number;
  misses: number;
  pushes: number;
  hitRate: number;
  roi: number;
  firstPick: string | null;
  lastPick: string | null;
}

export interface TierPerformance {
  tier: string;
  totalPicks: number;
  settledPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
}

export interface PropTypePerformance {
  propType: string;
  totalPicks: number;
  settledPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
}

export interface DirectionPerformance {
  direction: string;
  totalPicks: number;
  settledPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
}

export interface EdgePerformance {
  edge: string;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
}

export interface ConfidencePerformance {
  confidence: string;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
}

export interface TrendPerformance {
  trend: string;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
}

export interface PlayerPerformance {
  playerName: string;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
}

export interface ComboPerformance {
  tier: string;
  propType: string;
  direction: string;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
}

export interface ModelPerformance {
  model: string;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
}

export interface B2BPerformance {
  category: string;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
}

export interface BlowoutRiskPerformance {
  risk: string;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
}

export interface LineRangePerformance {
  lineRange: string;
  minLine: number;
  maxLine: number;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
  avgLine: number;
}

export interface PropTypeLineRangePerformance {
  propType: string;
  lineRange: string;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
  avgLine: number;
}

export interface LineDirectionPerformance {
  lineRange: string;
  direction: string;
  totalPicks: number;
  hits: number;
  misses: number;
  hitRate: number;
  roi: number;
  avgLine: number;
}

// Two-Tier Analytics Types
export interface HotColdPlayer {
  playerName: string;
  team: string | null;
  propType: string | null;
  recentHits: number;
  recentMisses: number;
  recentHitRate: number;
  streak: number; // Positive = win streak, negative = loss streak
  trend: 'hot' | 'cold' | 'neutral';
}

export interface RollingWindowPerformance {
  window: '7d' | '14d' | '30d' | 'all';
  totalPicks: number;
  hits: number;
  misses: number;
  pushes: number;
  hitRate: number;
  roi: number;
}

export interface PropTypeTrend {
  propType: string;
  overallHitRate: number;
  overallSample: number;
  recentHitRate: number;
  recentSample: number;
  trend: 'hot' | 'cold' | 'neutral';
  trendDelta: number;
}

export interface TwoTierAnalytics {
  hotColdPlayers: { hot: HotColdPlayer[]; cold: HotColdPlayer[] };
  rollingWindows: RollingWindowPerformance[];
  propTypeTrends: PropTypeTrend[];
}

export interface DashboardSummary {
  totalPicks: number;
  settledPicks: number;
  hits: number;
  misses: number;
  pushes: number;
  hitRate: number;
  bestConfig: string | null;
  bestConfigHitRate: number;
}

export interface LabDashboardData {
  summary: DashboardSummary;
  configPerformance: ConfigPerformance[];
  tierPerformance: TierPerformance[];
  propTypePerformance: PropTypePerformance[];
  directionPerformance: DirectionPerformance[];
  edgePerformance: EdgePerformance[];
  confidencePerformance: ConfidencePerformance[];
  trendPerformance: TrendPerformance[];
  modelPerformance: ModelPerformance[];
  comboPerformance: ComboPerformance[];
  b2bPerformance: B2BPerformance[];
  blowoutRiskPerformance: BlowoutRiskPerformance[];
  lineRangePerformance: LineRangePerformance[];
  propTypeLineRangePerformance: PropTypeLineRangePerformance[];
  lineDirectionPerformance: LineDirectionPerformance[];
  topPlayers: PlayerPerformance[];
  worstPlayers: PlayerPerformance[];
  filters: {
    availableConfigs: string[];
    availablePropTypes: string[];
  };
  generatedAt: string;
}

// =============================================================================
// Daily Performance Types
// =============================================================================

export interface DailyPick {
  playerName: string | null;
  propType: string;
  line: string | null;
  direction: string | null;
  tier: string | null;
  hit: boolean | null;
}

export interface DailyPerformance {
  date: string; // YYYY-MM-DD
  totalPicks: number;
  hits: number;
  misses: number;
  pushes: number;
  hitRate: number;
  roi: number;
  topPicks: DailyPick[];
}

// =============================================================================
// Settlement Types
// =============================================================================

export interface SettlementLegResult {
  legId: number;
  hit: boolean | null;
  actualValue: number | null;
  dnp?: boolean;
}

export interface SettlementGameDetail {
  gameId: string;
  legsInGame: number;
  legsSettled: number;
  error?: string | null;
  results?: SettlementLegResult[];
  exception?: boolean;
}

export interface SettlementError {
  gameId: string;
  error: string;
  legsAffected: number;
  affectedPlayers?: Array<{
    player: string;
    team: string | null;
    prop: string;
  }>;
}

/**
 * Detailed leg info for settlement results display
 */
export interface SettledLegDetail {
  id: number;
  player: string;
  team: string | null;
  prop: string;
  statType: string | null;
  line: string | null;
  overUnder: string | null;
  odds: string | null;
  hit: boolean | null;
  actualValue: string | null;
  dnp: boolean;
  voidReason: VoidReason | null;
  gameId: string | null;
  gameDate: string | null;
  opponentTeam: string | null;
}

/**
 * Detailed parlay info for settlement results display
 */
export interface SettledParlayDetail {
  id: number;
  userDisplayName: string | null;
  userEmail: string | null;
  tier: string | null;
  totalOdds: string | null;
  finalOdds: string | null; // Recalculated odds after voided legs removed
  stake: string | null;
  potentialPayout: string | null;
  result: string;
  actualPayout: string | null;
  legs: SettledLegDetail[];
  gameDate: string | null; // ISO string from JSON
  createdAt: string | null; // ISO string - when the parlay was tracked
}

/**
 * Pending leg info - a leg that couldn't be settled because game hasn't finished
 */
export interface PendingLegInfo {
  player: string;
  team: string | null;
  prop: string;
  odds: string | null;
  gameStatus: string;
}

/**
 * Pending parlay info - a parlay with legs that couldn't be settled because games haven't finished
 */
export interface PendingParlayDetail {
  parlayId: number;
  userDisplayName: string | null;
  userEmail: string | null;
  tier: string | null;
  totalOdds: string | null;
  legs: PendingLegInfo[];
}

/**
 * Prediction leg detail for settlement display
 */
export interface SettledPredictionLeg {
  playerName: string | null;
  team: string;
  propType: string;
  line: string | null;
  overUnder: string | null;
  hit: boolean | null;
  actualValue: string | null;
}

/**
 * Grouped prediction detail for settlement display (grouped by aiTier)
 */
export interface SettledPredictionDetail {
  aiTier: string;
  gameDate: string | null;
  legs: SettledPredictionLeg[];
  legsWon: number;
  legsLost: number;
}

/**
 * Pending prediction detail (game not finished yet)
 */
export interface PendingPredictionLegInfo {
  playerName: string | null;
  team: string;
  propType: string;
  line: string | null;
  overUnder: string | null;
}

/**
 * Pending predictions grouped by tier
 */
export interface PendingPredictionDetailGroup {
  aiTier: string;
  gameDate: string | null;
  legs: PendingPredictionLegInfo[];
}

export interface SettlementResult {
  success: boolean;
  message?: string;
  legsSettled: number;
  parlaysSettled: number;
  legsProcessed: number;
  parlaysUpdated: number;
  gamesProcessed?: number;
  errors?: SettlementError[];
  details?: SettlementGameDetail[];
  settledParlays?: SettledParlayDetail[];
  pendingParlays?: PendingParlayDetail[];
  // AI Predictions (ML training data)
  predictionsSettled?: number;
  settledPredictions?: SettledPredictionDetail[];
  pendingPredictions?: PendingPredictionDetailGroup[];
}

// =============================================================================
// Parlay Performance Types
// =============================================================================

// Parlay-level performance types
export interface ParlayLegInfo {
  id: number;
  player: string;
  prop: string;
  odds: string | null;
  hit: boolean | null;
}

export interface ParlayInfo {
  id: number;
  name: string | null;
  tier: string | null;
  totalOdds: string | null;
  legCount: number;
  result: string;
  unitsWon: number; // Positive for wins, negative for losses
  legs: ParlayLegInfo[];
}

export interface DailyParlayPerformance {
  date: string; // YYYY-MM-DD
  totalParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  pending: number;
  winRate: number;
  units: number; // Units won/lost based on actual parlay odds
  parlays: ParlayInfo[];
}

// =============================================================================
// Parlay Dashboard Types
// =============================================================================

export interface ParlayDashboardSummary {
  totalParlays: number;
  settledParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  pending: number;
  winRate: number;
  units: number;
}

export interface ParlayTierPerformance {
  tier: string;
  totalParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  units: number;
}

export interface ParlayLegCountPerformance {
  legCount: number;
  totalParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  units: number;
  avgOdds: number;
}

export interface ParlayConfidencePerformance {
  confidence: string;
  totalParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  units: number;
}

export interface ParlaySgpPerformance {
  isSgp: boolean;
  label: string;
  totalParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  units: number;
}

export interface ParlayOddsRangePerformance {
  oddsRange: string;
  minOdds: number;
  maxOdds: number;
  totalParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  units: number;
  avgOdds: number;
}

export interface ParlayOriginPerformance {
  origin: string;
  totalParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  units: number;
}

// Legacy alias
export type ParlaySourcePerformance = ParlayOriginPerformance;

export interface ParlayDayOfWeekPerformance {
  dayOfWeek: number;
  dayName: string;
  totalParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  units: number;
}

export interface ParlaySportsbookPerformance {
  sportsbook: string;
  sportsbookDisplay: string;
  totalParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  units: number;
}

export interface ParlayDashboardData {
  summary: ParlayDashboardSummary;
  tierPerformance: ParlayTierPerformance[];
  legCountPerformance: ParlayLegCountPerformance[];
  confidencePerformance: ParlayConfidencePerformance[];
  sgpPerformance: ParlaySgpPerformance[];
  oddsRangePerformance: ParlayOddsRangePerformance[];
  sourcePerformance: ParlaySourcePerformance[];
  dayOfWeekPerformance: ParlayDayOfWeekPerformance[];
  sportsbookPerformance: ParlaySportsbookPerformance[];
  generatedAt: string;
}

// =============================================================================
// Parlay Insights Types
// =============================================================================

export interface ParlayRollingWindow {
  window: '7d' | '14d' | '30d' | 'all';
  totalParlays: number;
  wins: number;
  losses: number;
  pushes: number;
  winRate: number;
  units: number;
}

export interface ParlayStreakInfo {
  currentStreak: { type: 'win' | 'loss'; count: number };
  longestWinStreak: number;
  longestLossStreak: number;
}

export interface ParlayInsights {
  rollingWindows: ParlayRollingWindow[];
  streakInfo: ParlayStreakInfo;
  bestLegCount: { legCount: number; winRate: number; sample: number } | null;
  bestTier: { tier: string; winRate: number; sample: number } | null;
}

// =============================================================================
// Leg Origin Performance Types
// =============================================================================

export interface LegOriginPerformance {
  origin: string;
  totalLegs: number;
  settledLegs: number;
  hits: number;
  misses: number;
  hitRate: number;
  verifiedBets: number;
}

export interface LegStatTypeByOrigin {
  statType: string;
  origin: string;
  totalLegs: number;
  hits: number;
  misses: number;
  hitRate: number;
}

// =============================================================================
// Market Insights Types
// =============================================================================

export interface MarketInsightLegStats {
  total: number;
  wins: number;
  hitRate: number;
  overCount: number;
  underCount: number;
  overWins: number;
  underWins: number;
  overPct: number;
  overHitRate: number;
  underHitRate: number;
  pendingLegs: number;
}

export interface MarketInsightParlayStats {
  total: number;
  wins: number;
  losses: number;
  hitRate: number;
  units: number;
  allOverParlays: number;
  allOverWins: number;
  allOverHitRate: number;
  allUnderParlays: number;
  allUnderWins: number;
  allUnderHitRate: number;
  mixedParlays: number;
  mixedWins: number;
  mixedHitRate: number;
}

export interface MarketInsightPropTypeStat {
  propType: string;
  total: number;
  wins: number;
  hitRate: number;
  overCount: number;
  overWins: number;
  overHitRate: number;
  underCount: number;
  underWins: number;
  underHitRate: number;
}

export interface MarketInsightBiasAssessment {
  overBiasPct: number;
  isBalanced: boolean;
  emoji: string;
}

export interface MarketInsightAI {
  explanation: string | null;
  recommendation: string | null;
  keyFactors: string[] | null;
}

// Situational stat with both leg-level and parlay-level metrics
export interface SituationalStat {
  label: string;
  // Leg-level
  legTotal: number;
  legWins: number;
  legHitRate: number;
  // Parlay-level
  parlayTotal: number;
  parlayWins: number;
  parlayHitRate: number;
  parlayUnits: number;
}

export interface MarketInsightSituationalStats {
  dayOfWeek: SituationalStat[];
  homeAway: SituationalStat[];
  spreadRange: SituationalStat[];
  b2bStatus: SituationalStat[];
  blowoutRisk: SituationalStat[];
}

// Player performance stat (leg-level only)
export interface PlayerStat {
  player: string;
  team: string;
  total: number;
  wins: number;
  hitRate: number;
}

export interface MarketInsightPlayerStats {
  hotPlayers: PlayerStat[];
  coldPlayers: PlayerStat[];
}

export interface DailyMarketInsight {
  date: string;
  marketCondition: MarketCondition;
  marketEmoji: string;
  insight: string;
  over: DirectionResult;
  under: DirectionResult;
  overall: DirectionResult;
  leagueConditions: LeagueMarketCondition[];
  legStats: MarketInsightLegStats;
  parlayStats: MarketInsightParlayStats;
  propTypeStats: MarketInsightPropTypeStat[];
  biasAssessment: MarketInsightBiasAssessment;
  ai: MarketInsightAI | null;
  // New situational and player stats
  situationalStats?: MarketInsightSituationalStats;
  playerStats?: MarketInsightPlayerStats;
}

/** Admin scope for market insights — simpler than user view (no user-specific scopes) */
export type AdminMarketScope = 'everything' | 'real_bets';

// =============================================================================
// Factor Performance Types
// =============================================================================

export interface FactorPerformanceRow {
  factor: string;
  column: string;
  enabledHits: number;
  enabledTotal: number;
  enabledRate: number;
  disabledHits: number;
  disabledTotal: number;
  disabledRate: number;
  lift: number;
}
